/**
 * /project-proposal-template — SEO template page for "project proposal
 * example/template" (~90 traffic, vol 7.1k, medium fit). Hands over the
 * proposal fields, then the Draftlize turn: a proposal is approved once on a
 * set of assumptions, then the project diverges from it as scope, budget,
 * and decisions move — and nothing reconciles the live project with the
 * approved document.
 *
 * English-only + inline. Chinese variant in ProjectProposalTemplateZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function ProjectProposalTemplate() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/project-proposal-template"
      documentTitle="Project Proposal Template + Example · Draftlize"
      documentDescription="A project proposal template you can copy — problem, objectives, scope, approach, timeline, budget, success criteria, risks — plus how to keep the live project from drifting from what was approved."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Template · Project proposals</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            A project proposal template<br/>
            <em>the project can&apos;t outrun.</em>
          </h1>
          <p className="lede">
            You came for a <em>project proposal template</em> — the structure that gets a project understood, scoped, and approved. It&apos;s below; copy it. But a proposal has a built-in expiry: it&apos;s approved once on a set of assumptions about scope, budget, and timeline, and then the project starts diverging from it the moment work begins. By month two the approved document and the live project quietly disagree, and nobody can say by how much. Draftlize gives you the same proposal, with its assumptions as cards that flag when reality moves past them.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The template</span>
            <h2 className="serif">Eight sections. <em>Copy them anywhere.</em></h2>
          </div>
          <p>A project proposal answers eight questions a decision-maker needs before they say yes. It works for an internal initiative or a client pitch — the shape is the same.</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'Problem / background', hint: 'Why bother', body: 'The situation or pain that justifies the project, before any solution. If you can\'t state the problem without naming your proposed work, you\'re pitching a solution looking for a problem.' },
            { k: 'Objectives & goals', hint: 'What success is', body: 'What the project will achieve, in measurable terms. The objectives the approver is really signing up to — keep them few and concrete enough to judge "did it work" against later.' },
            { k: 'Scope (in & out)', hint: 'Boundaries', body: 'What\'s included and — the field people skip — what\'s explicitly out. The "out of scope" list is what prevents the slow creep that blows timelines. Naming a non-goal is a decision, not an omission.' },
            { k: 'Approach / solution', hint: 'How', body: 'How you\'ll do it — the plan, the method, the phases. Enough to show it\'s feasible and thought-through, not a full spec. This is where a reviewer judges whether you actually know how to deliver.' },
            { k: 'Timeline & milestones', hint: 'When', body: 'The schedule with real checkpoints, not just an end date. Milestones are where you\'ll know if it\'s on track — and the honest places to re-decide if an assumption has changed.' },
            { k: 'Budget & resources', hint: 'What it costs', body: 'The money, people, and tools needed. Tie costs to the phases so the approver sees what they\'re buying at each stage, and so a scope change has a visible price.' },
            { k: 'Success criteria', hint: 'Done & worked', body: 'How everyone will agree the project succeeded — the metrics and acceptance conditions, decided up front before the result can bias the judgment.' },
            { k: 'Risks & assumptions', hint: 'What could break', body: 'The things that must hold for the plan to work, and what threatens it — named honestly, with mitigations. The assumptions here are exactly what the live project tends to silently outgrow.' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.06em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          Once approved, a proposal hands off to a <a href="/prd-template" style={{ color: 'var(--amber)' }}>PRD</a> and a <a href="/technical-design-document" style={{ color: 'var(--amber)' }}>technical design doc</a> — which inherit the same drift problem.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why a proposal <em>and the project diverge.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Approved once, then frozen</h3>
            <p>A proposal is signed off at a moment, on a set of assumptions. The project then moves — scope adjusts, a milestone slips, a cost changes — but the approved document sits frozen. Within weeks it describes a project that no longer exists, and it&apos;s the version leadership still thinks is true.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">No one reconciles live vs approved</h3>
            <p>Reconciling the running project against the proposal is a chore that loses to actually running the project. So the gap grows unwatched until a status review or a budget overrun forces the comparison — late, when the divergence is expensive to unwind.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Changed assumptions go untraced</h3>
            <p>A core assumption in the proposal turns out wrong in week six. Which parts of the plan rested on it — the timeline, the budget, the success criteria? In a document, nothing links them, so the assumption changes and the dependent commitments quietly stay as written.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same proposal, <em>wired in.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Each section is a card, each assumption linked</h3>
            <p>In Draftlize the proposal&apos;s scope, budget, timeline, and assumptions are cards — and the timeline links to the assumptions it depends on. The plan isn&apos;t a frozen PDF; it&apos;s a structure where the dependencies between &quot;we assume X&quot; and &quot;therefore the timeline is Y&quot; are real edges.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">An assumption changes, dependents flag stale</h3>
            <p>The week-six assumption breaks. Every commitment that rested on it — timeline, budget, success criteria — auto-flags stale, the way a build system invalidates everything downstream of a changed file. You re-decide deliberately, instead of discovering the divergence in a quarterly review.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your agent works from the live plan</h3>
            <p>When an AI agent drafts the PRD or status update over MCP, it reads the current proposal — the adjusted scope, the revised timeline — not the version approved before two assumptions changed. The approved doc and the real project stop being two different truths.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A proposal is approved once. The project keeps moving — and nothing tells the document it&apos;s describing a plan that no longer exists.
        </blockquote>
        <cite>Keep the eight sections. Let the assumptions flag the plan when they break.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What should a project proposal include?', a: 'A problem or background, measurable objectives, scope (in and out), the approach, a timeline with milestones, budget and resources, success criteria, and risks and assumptions. The most-skipped and most-valuable parts are the "out of scope" list and the named assumptions.' },
            { q: 'What is the difference between a project proposal and a PRD?', a: 'A project proposal makes the case for doing a project at all — justifying the investment, scope, and cost to a decision-maker. A PRD defines what to build once a project is approved. The proposal gets the yes; the PRD turns the yes into something a team can build.' },
            { q: 'How long should a project proposal be?', a: 'As short as it can be while answering the eight questions a decision-maker needs. For an internal initiative, one to three pages is often enough; a large client engagement may need more. Length is not the goal — a clear, decision-ready case is.' },
            { q: 'How do you keep a project from drifting from its proposal?', a: 'Tie the plan\'s commitments to the assumptions they rest on, and revisit them at milestones rather than only at the end. The failure mode is a proposal that is approved once and never reconciled against the live project until a review forces it.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Keep the plan and the project in sync <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Use the template above, or build the proposal as cards in Draftlize where the timeline and budget link to the assumptions they rest on — so when one breaks, the dependent commitments flag stale instead of quietly outliving the plan.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
